import type { MaybeRefOrGetter, Ref } from 'vue';
import { onScopeDispose, ref, toValue, watchEffect } from 'vue';

export interface SelectMenuFocusHandoffExpose {
	viewportRef?: HTMLDivElement | Readonly<{ value?: HTMLDivElement | null }>;
}

export interface UseSelectMenuFocusHandoffOptions {
	enabled: MaybeRefOrGetter<boolean>;
	selectMenuRef: Ref<SelectMenuFocusHandoffExpose | null>;
}

const FOCUS_SCOPE_UNMOUNT_EVENT = 'focusScope.autoFocusOnUnmount';

/**
 * Adapts Nuxt UI's SelectMenu focus lifecycle for transitions to another control.
 *
 * Nuxt UI through 4.10 nests an unconfigurable trapped Reka FocusScope inside SelectMenu.
 * Its delayed focus restoration can dismiss an overlay opened while the menu closes.
 * Keep this dependency workaround here until Nuxt UI exposes that focus-scope event.
 */
export function useSelectMenuFocusHandoff(options: UseSelectMenuFocusHandoffOptions) {
	const skipCloseAnimation = ref(false);
	let valueSelectedSinceOpen = false;
	let focusScope: HTMLElement | null = null;

	function preventFocusRestore(event: Event) {
		event.preventDefault();
		focusScope = null;
	}

	function syncFocusScope() {
		if (!toValue(options.enabled)) {
			removeFocusScopeListener();
			return;
		}

		const exposedViewport = options.selectMenuRef.value?.viewportRef;
		const viewport = exposedViewport instanceof HTMLDivElement ? exposedViewport : exposedViewport?.value;
		const nextFocusScope = viewport?.closest<HTMLElement>('[data-slot="focusScope"]') ?? null;
		if (!nextFocusScope || nextFocusScope === focusScope) return;

		removeFocusScopeListener();
		focusScope = nextFocusScope;
		focusScope.addEventListener(FOCUS_SCOPE_UNMOUNT_EVENT, preventFocusRestore, { once: true });
	}

	function removeFocusScopeListener() {
		focusScope?.removeEventListener(FOCUS_SCOPE_UNMOUNT_EVENT, preventFocusRestore);
		focusScope = null;
	}

	function onModelUpdate() {
		valueSelectedSinceOpen = true;
	}

	function onOpenUpdate(open: boolean) {
		if (open) {
			valueSelectedSinceOpen = false;
			skipCloseAnimation.value = false;
			return;
		}

		if (toValue(options.enabled) && !valueSelectedSinceOpen) {
			skipCloseAnimation.value = true;
		}
	}

	watchEffect(syncFocusScope, { flush: 'post' });
	onScopeDispose(removeFocusScopeListener);

	return {
		onModelUpdate,
		onOpenUpdate,
		skipCloseAnimation,
	};
}
